/**
 * MKS-393 — the "Merch sizes" card on the admin user detail page.
 *
 * Reads and writes `GET`/`PUT /api/admin/users/:id/merch-sizes`, a NARROW
 * endpoint of its own rather than more fields on the user form: that form
 * posts `PUT /api/admin/users/:id`, which is `requireAdmin` and stays
 * `requireAdmin`. The merch endpoint is `requireHR` and writes the six merch
 * columns only, so HR never gains write access to roles or dates as a side
 * effect of a merch ticket.
 *
 * NOTE on who reaches this today: `/admin/users/:id` is an `<AdminRoute>` in
 * `App.tsx`. Widening that page to HR is a separate decision with a much
 * larger blast radius than this ticket, so it was deliberately NOT done —
 * HR's own surface is `/hr/merch-sizes`.
 */
import { useEffect, useState } from 'react';
import { GiftIcon } from '@heroicons/react/24/outline';
import { Button } from '../common/Button';
import { MerchSizeFields } from '../profile/MerchSizeFields';
import { useUpdateUserMerchSizes, useUserMerchSizes } from '../../hooks/useMerchSizes';
import { MERCH_SIZE_FIELDS, type MerchSizes } from '../../constants/merchSizes';

const EMPTY: MerchSizes = {
  merchTshirtSize: null,
  merchTshirtCut: null,
  merchHoodieSize: null,
  merchHoodieCut: null,
  merchSockSize: null,
  merchShoeSize: null,
};

export function UserMerchSizesCard({ userId }: { userId: number }) {
  const { data, isLoading } = useUserMerchSizes(userId);
  const update = useUpdateUserMerchSizes(userId);
  const stored = data ?? EMPTY;

  const [draft, setDraft] = useState<MerchSizes>(stored);
  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  const dirty = MERCH_SIZE_FIELDS.some((field) => draft[field] !== stored[field]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <GiftIcon className="h-5 w-5 text-gray-400" />
        <div>
          <h2 className="text-lg font-medium text-gray-900">Merch sizes</h2>
          <p className="text-sm text-gray-500 mt-1">
            Company swag sizes. People normally fill these in themselves on their profile —
            edit here only when someone asks you to.
          </p>
        </div>
      </div>

      <MerchSizeFields
        value={draft}
        disabled={isLoading || update.isPending}
        onChange={(patch) => setDraft((previous) => ({ ...previous, ...patch }))}
      />

      <div className="mt-4 flex items-center gap-2">
        <Button onClick={() => update.mutate(draft)} disabled={!dirty || update.isPending}>
          {update.isPending ? 'Saving...' : 'Save sizes'}
        </Button>
        {dirty && (
          <Button variant="secondary" onClick={() => setDraft(stored)} disabled={update.isPending}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}
